"use client";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import type { FC, ReactNode } from "react";
import { useInView } from "../../../hooks/useInView";
import { ReadMore } from "../../../components/content/read-more/ReadMore";
import { Text } from "../../content/text/Text";
import { Box } from "../../layout/box/Box";
import { Flex } from "../../layout/flex/Flex";
import { NextImage } from "../../media-and-icons/image/NextImage";
import { resolveGridBackgroundStyles } from "./gridBackgroundStyles";
import { styles } from "./StoryTileStyles";

export type StoryTileGridBackground = "Gray" | "White" | "Black";

interface ITileStyles {
    tileBg?: string;
    tilePaddingX: (string | number)[] | number;
    textColor: string;
}

const DEFAULT_TILE_STYLES: ITileStyles = { tileBg: undefined, tilePaddingX: 0, textColor: "black" };

const TILE_STYLES_BY_GRID_BG: Partial<Record<StoryTileGridBackground, ITileStyles>> = {
    White: DEFAULT_TILE_STYLES,
    Gray: { tileBg: "white", tilePaddingX: [4, 6], textColor: "black" },
    Black: { tileBg: "black", tilePaddingX: 0, textColor: "white" },
};

interface IStoryTileProps {
    imageSrc: string;
    imageAlt: string;
    label?: string;
    headline?: string;
    description?: string;
    onInView?: () => void;
    links?: ReactNode | null | undefined;
    whiteBackground?: boolean;
    activateReadMore?: boolean;
    showMoreLabel?: string;
    showLessLabel?: string;
    coloredBar?: string;
    gridBackground?: StoryTileGridBackground;
    isCarousel?: boolean;
    tileCount?: number;
}

const SquareImage: FC<{
    imageSrc: string;
    imageAlt: string;
    imageSizes: string[];
    whiteBackground: boolean;
    hiddenOnMobile?: boolean;
}> = ({ imageSrc, imageAlt, imageSizes, whiteBackground, hiddenOnMobile }) => (
    <Box __css={hiddenOnMobile ? styles.squareImageHiddenOnMobile : styles.squareImage}>
        <Box {...styles.imageFill}>
            {whiteBackground ? (
                <Box {...styles.imagePlaceholder} />
            ) : (
                <NextImage
                    src={imageSrc}
                    alt={imageAlt}
                    sizes={imageSizes}
                    fill
                    objectFit="cover"
                />
            )}
        </Box>
    </Box>
);

export const StoryTile: FC<IStoryTileProps> = ({
    imageSrc,
    imageAlt,
    label,
    headline,
    description,
    onInView,
    links = null,
    whiteBackground = false,
    activateReadMore,
    showMoreLabel,
    showLessLabel,
    coloredBar,
    gridBackground = "White",
    isCarousel = false,
    tileCount,
}) => {
    const { tileBg, tilePaddingX, textColor } = resolveGridBackgroundStyles(
        TILE_STYLES_BY_GRID_BG,
        DEFAULT_TILE_STYLES,
        gridBackground
    );
    const isXl = useBreakpoint("xl");
    const isGridCard = !isCarousel && (tileCount === 2 || tileCount === 3);
    const isCarouselCard = isCarousel;
    const isCardLayout = isGridCard || isCarouselCard;
    const useLegacyImageLayout = !isCardLayout;
    const imageSizes = isCarouselCard
        ? ["85vw", "30vw"]
        : tileCount === 3
          ? ["100vw", "33vw"]
          : tileCount === 2
            ? ["100vw", "50vw"]
            : ["100vw", "100vw"];

    const { ref } = useInView({
        threshold: 0.5,
        onChange: (inView) => {
            if (inView && onInView) {
                setTimeout(() => onInView(), 2000);
            }
        },
    });
    const onClickReadMore = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const hideImageBox = whiteBackground && Boolean(coloredBar);
    const onlyHeadline = headline && !label && !description && !links;

    const legacyImageBox = !hideImageBox && useLegacyImageLayout && (
        <Box
            position="relative"
            w="full"
            aspectRatio="1/1"
            display={whiteBackground ? { base: "none", xl: "block" } : "block"}
        >
            {whiteBackground ? (
                <>
                    <Box bg="white" position="absolute" inset={0} />
                    {onlyHeadline && (
                        <Flex position="absolute" inset={0} alignItems="center" px={6}>
                            <Text as="h2" color={textColor} {...styles.headlineDesktop}>
                                {headline}
                            </Text>
                        </Flex>
                    )}
                </>
            ) : (
                <NextImage
                    src={imageSrc}
                    alt={imageAlt}
                    sizes={["100vw", "100vw"]}
                    fill
                    objectFit="cover"
                />
            )}
        </Box>
    );

    const cardImageBox = !hideImageBox && isCardLayout && (
        <SquareImage
            imageSrc={imageSrc}
            imageAlt={imageAlt}
            imageSizes={imageSizes}
            whiteBackground={whiteBackground}
            hiddenOnMobile={whiteBackground && !isCarouselCard}
        />
    );

    const labelStyles = isCardLayout ? styles.label3col : styles.labelDefault;

    const labelElement = label ? (
        <Box as="h3" aria-label={label} {...styles.label} {...labelStyles} color={textColor}>
            {label}
        </Box>
    ) : (
        <Box
            {...styles.labelSpacer}
            {...(isCardLayout ? styles.labelSpacerCard : styles.labelSpacerDefault)}
            aria-hidden
        />
    );

    const headlineElement = headline && (
        <Text
            as="h2"
            fontWeight="bold"
            display={
                useLegacyImageLayout && whiteBackground ? { base: "block", xl: "none" } : "block"
            }
            color={textColor}
            {...(isGridCard
                ? styles.headline3col
                : isCarouselCard
                  ? styles.headlineCarousel
                  : useLegacyImageLayout && whiteBackground
                    ? styles.headlineMobile
                    : styles.headlineDefault)}
        >
            {headline}
        </Text>
    );

    const descriptionElement = description && (
        <ReadMore
            __css={styles.readMore}
            onClick={onClickReadMore}
            showMoreLabel={showMoreLabel}
            showLessLabel={showLessLabel}
            collapsedHeight={isCarouselCard ? ["72px", "84px"] : ["4rem"]}
            activateReadMore={isCardLayout ? false : activateReadMore}
            text={description}
            textStyle={{
                ...(isCarouselCard
                    ? styles.descriptionCarousel
                    : isGridCard
                      ? styles.description3col
                      : styles.description),
                color: isCardLayout ? undefined : textColor,
            }}
        />
    );

    const textContent = isCardLayout ? (
        <Box
            {...(isGridCard
                ? styles.textContentGrid
                : isXl
                  ? styles.textContentCarouselDesktop
                  : styles.textContentCarouselMobile)}
            pl={isGridCard ? tilePaddingX : undefined}
        >
            <Flex {...styles.textContentInner}>
                {labelElement}
                {(headline || description) && (
                    <Flex {...styles.textGroup}>
                        {headlineElement}
                        {descriptionElement}
                    </Flex>
                )}
            </Flex>
            {links && (
                <Box {...(isGridCard ? styles.linksGrid : styles.linksCarousel)}>{links}</Box>
            )}
        </Box>
    ) : (
        <Box {...styles.textContentDefault} px={tilePaddingX}>
            <Flex {...styles.textContentInnerDefault}>
                {labelElement}
                {headlineElement}
                {descriptionElement}
            </Flex>
            {links && <Box {...styles.linksDefault}>{links}</Box>}
        </Box>
    );

    return (
        <Flex
            ref={ref}
            {...(isCarouselCard
                ? isXl
                    ? styles.rootCarouselCardDesktop
                    : styles.rootCarouselCardMobile
                : isGridCard
                  ? { ...styles.rootCard, bg: tileBg }
                  : { ...styles.rootDefault, bg: tileBg })}
        >
            {legacyImageBox}
            {cardImageBox}
            {coloredBar && <Box bg={coloredBar} {...styles.coloredBar} />}
            {textContent}
        </Flex>
    );
};
